/**
 * Página IntensiveStudy - Fase 2
 * Página principal de sesiones intensivas con 5 vistas:
 * 1. Configuración - Seleccionar tema e intensidad
 * 2. Sesión Activa - Timer Pomodoro + tarjetas
 * 3. Descanso - Timer de descanso
 * 4. Pausa - Estado pausado
 * 5. Resultados - Resumen post-sesión
 *
 * IMPLEMENTACIÓN: Timer sincronizado con backend usando timestamps UTC
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  ArrowLeft,
  Play,
  Pause,
  Square,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  X,
  Check,
  Eye,
  EyeOff,
} from "lucide-react";

import { useIntensiveSessions } from "../../hooks/useIntensiveSessions";
import { useTopics } from "../../hooks/useTopics";
import { usePomodoroTimer } from "../../hooks/usePomodoroTimer";

import PomodoroTimer from "../components/PomodoroTimer";
import IntensityPicker from "../components/IntensityPicker";
import SessionDifficultySelector from "../components/SessionDifficultySelector";
import SessionResultsSummary from "../components/SessionResultsSummary";

import {
  StudyIntensity,
  CardDifficulty,
  POMODORO_CONFIG,
  PomodoroStatus,
  IntensiveSessionDetail,
  IntensiveStudySession,
  IntensiveResumeSnapshot,
  PomodoroBlock,
  AbandonInfo,
  SessionCompletionResult,
  SessionCompletionSummary,
} from "../types/intensiveSessions";
import {
  buildTimerDescriptor,
  resumeViewFor,
  shouldShowBlockComplete,
} from "../features/intensive-study/state/resume";
import { resolveCompletionDispatch } from "../features/intensive-study/state/timerCompletion";
import {
  selectVisibleIntensiveError,
  startFailureRecovery,
  type IntensiveCommand,
  type IntensiveCommandFailure,
  type IntensiveMutationOutcome,
} from "../features/intensive-study/state/mutationOutcome";
import { createSingleFlight } from "../features/intensive-study/state/singleFlight";
import { resolveTotalBlocks } from "../features/intensive-study/state/blockProgress";
import type { IntensiveError } from "../features/intensive-study/api/errors";
import { loadSessionBadges } from "../features/intensive-study/api/sessionBadges";
import { IntradayReview } from "../types/intradayReviews";
import { UserBadge } from "../types/gamification";

// Tipo de vista interna
type SessionView =
  | "CONFIG"
  | "READY"
  | "ACTIVE"
  | "BREAK"
  | "PAUSED"
  | "RESULTS";

const IntensiveStudy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Hooks
  const {
    sessions,
    currentSession,
    sessionTotalBlocks,
    currentPomodoro,
    currentCard,
    loading,
    error,
    createSession,
    fetchSessions,
    fetchSessionDetail,
    startSession,
    pauseSession,
    abandonSession,
    getAbandonInfo,
    completeSession,
    retryCompleteSession,
    retryAbandonSession,
    getActiveSession,
    rehydrateSession,
    reloadSession,
    resumeFromPause,
    startPomodoro,
    completePomodoro,
    endBreak,
    skipBreak,
    getNextCard,
    completeCard,
    clearError,
  } = useIntensiveSessions();

  const {
    topics,
    fetchUserTopics: fetchTopics,
    loading: loadingTopics,
  } = useTopics();

  const pomodoroTimer = usePomodoroTimer();
  const totalBlocks = resolveTotalBlocks({
    statusTotalBlocks: sessionTotalBlocks,
    session: currentSession,
    blockNumber: pomodoroTimer.blockNumber,
  });

  // Estado local
  const [currentView, setCurrentView] = useState<SessionView>("CONFIG");
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [selectedIntensity, setSelectedIntensity] =
    useState<StudyIntensity | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<CardDifficulty | null>(null);
  const [abandonInfo, setAbandonInfo] = useState<AbandonInfo | null>(null);
  const [sessionResults, setSessionResults] =
    useState<IntensiveSessionDetail | null>(null);
  const [sessionSummary, setSessionSummary] =
    useState<SessionCompletionSummary | null>(null);
  const [newBadges, setNewBadges] = useState<UserBadge[]>([]);
  const [intradayReviews, setIntradayReviews] = useState<IntradayReview[]>([]);
  const [activeSessionError, setActiveSessionError] = useState<string | null>(
    null,
  );
  // A lifecycle command that was rejected or lost. While it is set the local
  // lifecycle stays where it was and the user can retry.
  const [commandFailure, setCommandFailure] =
    useState<IntensiveCommandFailure | null>(null);
  // Set when a start found no block left to run: the way forward is
  // completing the session, not retrying the start.
  const [completionOffered, setCompletionOffered] = useState(false);
  // True only once authoritative session/block/card/timer data has been applied.
  const [hydrated, setHydrated] = useState(false);
  const [resuming, setResuming] = useState(false);

  // Cargar temas al montar
  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // Obtener sesiones previas al montar
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Verificar si hay sesión activa al montar
  useEffect(() => {
    const checkActiveSession = async () => {
      const active = await getActiveSession();
      if (active) {
        setActiveSessionError(
          t(
            "intensiveStudy.activeSessionExists",
            "Ya tienes una sesión activa. Continúa o abandónala primero.",
          ),
        );
      }
    };
    checkActiveSession();
  }, [getActiveSession, t]);

  // ==================== FUNCIONES DE SINCRONIZACIÓN ====================

  // Sincronizar timer con backend (para pausas y resume)
  // Usa el timestamp UTC del backend para calcular tiempo restante exacto
  const syncTimerWithBackend = useCallback(() => {
    if (!currentPomodoro) return;

    const timer = buildTimerDescriptor(
      currentPomodoro,
      currentPomodoro.status === PomodoroStatus.ON_BREAK ? "BREAK" : "ACTIVE",
    );

    if (timer) {
      pomodoroTimer.syncWithBackend(timer.startedAt, timer.durationSeconds);
    }
  }, [currentPomodoro, pomodoroTimer]);

  // Arrancar un bloque de trabajo en una sola transición atómica.
  // La duración viaja con la transición, por lo que arrancar no depende del
  // tiempo restante del bloque anterior (que ya puede ser cero).
  const startWorkBlock = useCallback(
    (block: PomodoroBlock) => {
      pomodoroTimer.transitionTo({
        phase: "WORK",
        durationSeconds:
          block.durationMinutes !== undefined && block.durationMinutes !== null
            ? block.durationMinutes * 60 // Convertir minutos a segundos
            : undefined,
        startedAt: block.startedAt ?? null,
      });
    },
    [pomodoroTimer],
  );

  // Manejar la expiración natural del timer.
  // Se escucha la señal de expiración del hook, no el valor cero: hidratar un
  // bloque ya vencido deja el timer en cero sin emitir señal, así que la
  // reanudación nunca fabrica una transición en el backend.
  const lastHandledCompletionRef = useRef(0);
  // Identifies the results view a badge read belongs to, so a late answer
  // never lands on a later session.
  const badgeRequestRef = useRef(0);
  // Single-flight lock for handlePomodoroComplete: the expiry effect, the
  // break panel button, and the card-complete path can all reach it. A
  // second call landing while the first is still awaiting must not restart
  // the break at full duration or fire a redundant POST. The instance must
  // stay stable across renders — the ref IS the lock.
  const completePomodoroOnce = useRef(createSingleFlight()).current;
  useEffect(() => {
    const { dispatch, nextHandledTick } = resolveCompletionDispatch({
      completionTick: pomodoroTimer.completionTick,
      lastHandledTick: lastHandledCompletionRef.current,
      view: currentView,
    });

    // Always consume the tick, even when it cannot be dispatched, so a stale
    // expiry raised outside a live view (e.g. after an abandon) cannot latch
    // and fire on a later, unrelated session (REL-201).
    lastHandledCompletionRef.current = nextHandledTick;

    if (!dispatch) return;

    if (dispatch === "ACTIVE") {
      void handlePomodoroComplete();
    } else {
      void handleBreakEnd();
    }
  }, [pomodoroTimer.completionTick, currentView]);

  // Efecto para sincronización periódica con backend
  // Sincroniza cada 30 segundos para mantener el timer preciso
  useEffect(() => {
    let syncInterval: NodeJS.Timeout | null = null;

    if (
      (currentView === "ACTIVE" || currentView === "BREAK") &&
      currentPomodoro
    ) {
      // Sincronizar cada 30 segundos
      syncInterval = setInterval(() => {
        syncTimerWithBackend();
      }, 30000);
    }

    return () => {
      if (syncInterval) {
        clearInterval(syncInterval);
      }
    };
  }, [currentView, currentPomodoro, syncTimerWithBackend]);

  // ==================== REANUDACIÓN AUTORITATIVA ====================

  /**
   * Apply an authoritative snapshot: block number, phase, absolute timer and
   * view. Nothing is derived locally, so a resumed session can progress.
   */
  const applyResumeSnapshot = useCallback(
    (snapshot: IntensiveResumeSnapshot) => {
      if (snapshot.block) {
        pomodoroTimer.setBlockNumber(snapshot.block.blockNumber);
      }

      if (snapshot.timer) {
        // Transición atómica sobre los anclajes absolutos del backend. Si el
        // bloque ya venció, el timer queda en cero y pausado de forma
        // determinista: la transición la sigue decidiendo el backend.
        pomodoroTimer.transitionTo({
          phase: snapshot.timer.phase,
          durationSeconds: snapshot.timer.durationSeconds,
          startedAt: snapshot.timer.startedAt,
        });
      } else {
        // No live backend timer: never invent one.
        pomodoroTimer.pause();
      }

      setShowAnswer(false);
      setSelectedDifficulty(null);
      setActiveSessionError(null);
      setHydrated(true);
      setCurrentView(resumeViewFor(snapshot.phase));
    },
    [pomodoroTimer],
  );

  /**
   * Continue the live session: discover it, hydrate it from the backend and
   * only then render an actionable view.
   */
  const handleContinueSession = useCallback(async () => {
    setResuming(true);
    setHydrated(false);

    try {
      const active = await getActiveSession();
      if (!active) {
        setActiveSessionError(null);
        return;
      }

      const snapshot = await rehydrateSession(active.id);
      if (!snapshot) {
        return;
      }

      applyResumeSnapshot(snapshot);
    } catch (err) {
      console.error("Error continuing session:", err);
    } finally {
      setResuming(false);
    }
  }, [getActiveSession, rehydrateSession, applyResumeSnapshot]);

  // ==================== FUNCIONES DE MANEJO ====================

  // Crear y empezar sesión
  const handleCreateSession = async () => {
    if (!selectedTopicId || !selectedIntensity) return;

    setActiveSessionError(null);

    try {
      // Crear sesión
      const newSession = await createSession(
        selectedTopicId,
        selectedIntensity,
      );
      if (newSession) {
        // Iniciar sesión (cambia estado a ACTIVE)
        const startedSession = await startSession(newSession.id);
        if (startedSession) {
          // Obtener detalle de la sesión
          await fetchSessionDetail(startedSession.id);

          // Configurar timer en 0, pausado - esperando a que inicie el Pomodoro
          pomodoroTimer.reset();
          pomodoroTimer.setPhase("WORK");

          // Cambiar a vista READY (sesión lista, esperando iniciar Pomodoro)
          setCurrentView("READY");
        }
      }
    } catch (err: any) {
      // Manejar error específico de sesión activa existente
      if (err.code === "ACTIVE_SESSION_EXISTS") {
        setActiveSessionError(err.message);
      } else {
        console.error("Error creating session:", err);
      }
    }
  };

  // Iniciar primer bloque Pomodoro
  const handleStartFirstPomodoro = async () => {
    if (!currentSession) return;

    try {
      await startBlock(currentSession.id);
    } catch (err) {
      console.error("Error starting first pomodoro:", err);
    }
  };

  // Pausar sesión
  // La vista sólo cambia con una pausa confirmada por el backend.
  const handlePause = async () => {
    if (!currentSession) return;

    setCommandFailure(null);
    const outcome = await pauseSession(currentSession.id);

    if (outcome.status !== "success") {
      setCommandFailure({ command: "PAUSE", message: outcome.error.message });
      return;
    }

    if (outcome.snapshot) {
      applyResumeSnapshot(outcome.snapshot);
      return;
    }

    pomodoroTimer.pause();
    setCurrentView("PAUSED");
  };

  // Reanudar sesión
  const handleResume = async () => {
    if (!currentSession) return;

    // A pending failure from a different command (e.g. a failed Abandon)
    // must not survive into the resumed view.
    setCommandFailure(null);
    setResuming(true);
    setHydrated(false);

    try {
      // Reconciles with an authoritative GET before ever replaying the
      // non-idempotent /start command, so a retry after a landed-but-
      // unhydrated start never re-POSTs.
      const snapshot = await resumeFromPause(currentSession.id);
      if (!snapshot) return;

      applyResumeSnapshot(snapshot);
    } catch (err) {
      console.error("Error resuming session:", err);
    } finally {
      setResuming(false);
    }
  };

  // Enviar el abandono ya confirmado. Se reutiliza al reintentar, para no
  // volver a pedir confirmación de una decisión que el usuario ya tomó.
  const runAbandon = async (
    send: (
      id: number,
    ) => Promise<IntensiveMutationOutcome<IntensiveStudySession>>,
  ) => {
    if (!currentSession) return;

    setCommandFailure(null);
    const outcome = await send(currentSession.id);

    if (outcome.status !== "success") {
      setCommandFailure({ command: "ABANDON", message: outcome.error.message });
      return;
    }

    // Stop the timer at the source so an orphan interval cannot keep running
    // after the view leaves ACTIVE/BREAK (REL-201). Pausing only after the
    // backend confirms keeps a failed abandon from freezing a live session.
    pomodoroTimer.pause();

    setCurrentView("RESULTS");
  };

  // Abandonar sesión
  const handleAbandon = async () => {
    if (!currentSession) return;

    const info = await getAbandonInfo(currentSession.id);
    setAbandonInfo(info);

    // Confirmar abandono
    if (
      window.confirm(
        info?.message ||
          t(
            "intensiveStudy.confirmAbandon",
            "¿Estás seguro de abandonar la sesión? Perderás XP.",
          ),
      )
    ) {
      await runAbandon(abandonSession);
    }
  };

  // Completar Pomodoro
  const handlePomodoroComplete = async () => {
    if (!currentSession || !currentPomodoro) return;

    await completePomodoroOnce(async () => {
      setCommandFailure(null);

      // The work block is over in substance: stop the countdown before the
      // POST so a failed completion never keeps burning work time.
      pomodoroTimer.pause();

      const sessionId = currentPomodoro.sessionId;
      const outcome = await completePomodoro(sessionId, currentPomodoro.id);

      if (outcome.status !== "success") {
        // Stay in ACTIVE: the backend still holds the block ACTIVE, so a
        // local break would count down time the backend never granted.
        setCommandFailure({
          command: "COMPLETE_BLOCK",
          message: outcome.error.message,
        });
        return;
      }

      if (outcome.snapshot) {
        // Ambiguous failure resolved as landed: adopt the authoritative break.
        await adoptReconciledBreak(outcome.snapshot, sessionId);
        return;
      }

      // Determinar tipo de descanso
      const isLongBreak =
        pomodoroTimer.blockNumber % POMODORO_CONFIG.BLOCKS_UNTIL_LONG_BREAK ===
        0;
      const nextPhase = isLongBreak ? "LONG_BREAK" : "SHORT_BREAK";

      // Iniciar descanso en una sola transición: la duración viaja con ella,
      // por lo que el cero del bloque recién vencido no bloquea el arranque.
      pomodoroTimer.transitionTo({ phase: nextPhase });
      setCurrentView("BREAK");
    });
  };

  // Retry a failed block completion without ever replaying a landed POST:
  // reconcile with an authoritative GET first, replay only if still ACTIVE.
  const retryPomodoroComplete = async () => {
    if (!currentSession) return;

    setCommandFailure(null);
    const snapshot = await rehydrateSession(currentSession.id);

    // Any authoritative phase other than a still-ACTIVE block means either the
    // original completion landed or the session moved on out of band. Adopt
    // that state; re-sending would only earn a rejection the user cannot act on.
    if (snapshot && snapshot.phase !== "ACTIVE") {
      await adoptReconciledBreak(snapshot, currentSession.id);
      return;
    }

    await handlePomodoroComplete();
  };

  // Arrancar un bloque de trabajo (el primero, o el siguiente una vez que el
  // descanso quedó cerrado). La vista y el número de bloque sólo avanzan
  // con un inicio confirmado; un inicio rechazado se recupera según su código.
  const startBlock = async (sessionId: number) => {
    setCommandFailure(null);
    setCompletionOffered(false);

    const outcome = await startPomodoro(sessionId);

    if (outcome.status !== "success") {
      await recoverFailedStart(sessionId, outcome.error);
      return;
    }

    if (outcome.snapshot) {
      // Reconciled start: adopt whatever phase the backend reports.
      await adoptReconciledBreak(outcome.snapshot, sessionId);
      return;
    }

    const block = outcome.data;
    pomodoroTimer.setBlockNumber(
      block.blockNumber ?? pomodoroTimer.blockNumber + 1,
    );

    // Sincronizar con la duración del nuevo Pomodoro del backend
    startWorkBlock(block);

    // Cargar la primera tarjeta del nuevo bloque
    await getNextCard(sessionId);

    setHydrated(true);
    setCurrentView("ACTIVE");
  };

  // Recuperar un inicio rechazado sin dejar la vista varada.
  const recoverFailedStart = async (
    sessionId: number,
    error: IntensiveError,
  ) => {
    switch (startFailureRecovery(error)) {
      case "RELOAD":
        // The session moved on (paused, ended, block running or on break):
        // its authoritative phase replaces the local one.
        await adoptReloadedSession(sessionId, error.message);
        return;
      case "OFFER_COMPLETION":
        pomodoroTimer.pause();
        setCompletionOffered(true);
        return;
      case "UNAVAILABLE":
        // The hook already shows the unavailable message; nothing to retry.
        return;
      case "RETRY":
        setCommandFailure({ command: "START", message: error.message });
        return;
    }
  };

  // Recargar la sesión y adoptar su fase; un fallo de lectura es reintentable.
  const adoptReloadedSession = async (sessionId: number, message: string) => {
    const reload = await reloadSession(sessionId);
    if (reload.status === "found") {
      await adoptReconciledBreak(reload.snapshot, sessionId);
    } else if (reload.status === "failed") {
      setCommandFailure({
        command: "START",
        message: reload.error.message || message,
      });
    }
  };

  // Reintentar un inicio sin repetir uno que ya aterrizó: leer primero y
  // volver a iniciar sólo si la sesión sigue lista para un bloque nuevo.
  const retryStart = async () => {
    if (!currentSession) return;

    const sessionId = currentSession.id;
    setCommandFailure(null);
    const reload = await reloadSession(sessionId);

    if (reload.status === "unavailable") return;
    if (reload.status === "failed") {
      setCommandFailure({ command: "START", message: reload.error.message });
      return;
    }
    if (reload.snapshot.phase !== "READY") {
      await adoptReconciledBreak(reload.snapshot, sessionId);
      return;
    }

    await startBlock(sessionId);
  };

  // Adoptar el estado autoritativo tras reconciliar un comando ambiguo.
  const adoptReconciledBreak = async (
    snapshot: IntensiveResumeSnapshot,
    sessionId: number,
  ) => {
    applyResumeSnapshot(snapshot);
    if (snapshot.phase === "ACTIVE") {
      await getNextCard(sessionId);
    }
  };

  // Terminar descanso
  const handleBreakEnd = async () => {
    if (!currentSession || !currentPomodoro) return;

    setCommandFailure(null);
    const sessionId = currentSession.id;

    // Terminar descanso en backend
    const outcome = await endBreak(sessionId, currentPomodoro.id);

    if (outcome.status !== "success") {
      setCommandFailure({
        command: "END_BREAK",
        message: outcome.error.message,
      });
      return;
    }

    pomodoroTimer.pause();

    if (outcome.snapshot) {
      await adoptReconciledBreak(outcome.snapshot, sessionId);
      return;
    }

    await startBlock(sessionId);
  };

  // Saltar descanso
  const handleSkipBreak = async () => {
    if (!currentSession || !currentPomodoro) return;

    setCommandFailure(null);
    const sessionId = currentSession.id;

    const outcome = await skipBreak(sessionId, currentPomodoro.id);

    if (outcome.status !== "success") {
      setCommandFailure({
        command: "SKIP_BREAK",
        message: outcome.error.message,
      });
      return;
    }

    if (outcome.snapshot) {
      await adoptReconciledBreak(outcome.snapshot, sessionId);
      return;
    }

    await startBlock(sessionId);
  };

  // Completar tarjeta
  const handleCardComplete = async () => {
    if (!currentSession || !currentCard || !selectedDifficulty) return;

    try {
      await completeCard(
        currentSession.id,
        currentCard.cardId,
        selectedDifficulty,
      );
      setShowAnswer(false);
      setSelectedDifficulty(null);

      // Obtener siguiente tarjeta
      const result = await getNextCard(currentSession.id);

      if (!result.card) {
        // No hay más tarjetas en este bloque
        if (result.sessionComplete) {
          // La sesión está completa (todos los bloques terminados)
          await handleSessionComplete();
        } else if (result.blockComplete && currentPomodoro) {
          // El bloque actual está completo, pasar a descanso
          // Completar el Pomodoro actual y mostrar vista de descanso
          await handlePomodoroComplete();
        }
        // Si no es ni blockComplete ni sessionComplete, esperar al timer
      }
    } catch (err) {
      console.error("Error completing card:", err);
    }
  };

  // Completar sesión
  const finishSession = async (
    send: (
      id: number,
    ) => Promise<IntensiveMutationOutcome<SessionCompletionResult>>,
  ) => {
    if (!currentSession) return;

    setCommandFailure(null);
    const outcome = await send(currentSession.id);

    if (outcome.status !== "success") {
      setCommandFailure({
        command: "COMPLETE",
        message: outcome.error.message,
      });
      return;
    }

    pomodoroTimer.pause();
    setSessionResults(
      outcome.snapshot ? outcome.snapshot.session : outcome.data.session,
    );
    // A reconciled completion carries no summary; the results view then falls
    // back to the authoritative session relations.
    setSessionSummary(outcome.data.summary);
    setIntradayReviews(outcome.data.summary?.nextReviews ?? []);
    setNewBadges([]);
    setCurrentView("RESULTS");

    // Badges are awarded after the commit and are not part of the response.
    // Reading them is best effort: the session is already complete.
    const request = ++badgeRequestRef.current;
    const completed = outcome.snapshot
      ? outcome.snapshot.session
      : outcome.data.session;
    loadSessionBadges({
      badgeEvaluation: outcome.data.badgeEvaluation,
      sessionStartedAt: completed?.startedAt,
    })
      .catch(() => [])
      .then((badges) => {
        if (badgeRequestRef.current === request) setNewBadges(badges);
      });
  };

  const handleSessionComplete = () => finishSession(completeSession);
  // Completar la sesión ofrecida tras un inicio sin bloques pendientes.
  const completeOfferedSession = () => {
    setCompletionOffered(false);
    return handleSessionComplete();
  };
  // Retry a failed completion without ever replaying a landed POST (RES-203).
  const retrySessionComplete = () => finishSession(retryCompleteSession);

  // Reintentar el último comando fallido sin repetir pasos ya confirmados.
  const retryCommand = (command: IntensiveCommand) => {
    switch (command) {
      case "PAUSE":
        void handlePause();
        return;
      case "ABANDON":
        void runAbandon(retryAbandonSession);
        return;
      case "COMPLETE":
        void retrySessionComplete();
        return;
      case "END_BREAK":
        void handleBreakEnd();
        return;
      case "SKIP_BREAK":
        void handleSkipBreak();
        return;
      case "COMPLETE_BLOCK":
        void retryPomodoroComplete();
        return;
      case "START":
        void retryStart();
        return;
    }
  };

  // Descartar el error visible sin tocar el ciclo de vida de la sesión.
  const dismissCommandFailure = () => {
    setCommandFailure(null);
    clearError();
  };

  // Volver a configuración
  const handleBackToConfig = () => {
    badgeRequestRef.current += 1;
    setNewBadges([]);
    setCurrentView("CONFIG");
    setSelectedTopicId(null);
    setSelectedIntensity(null);
    setShowAnswer(false);
    setSelectedDifficulty(null);
    setHydrated(false);
    setCommandFailure(null);
    setCompletionOffered(false);
    pomodoroTimer.reset();
    clearError();
  };

  // Calcular progreso
  const getProgress = () => {
    if (!currentSession) return 0;
    const completed =
      currentSession.sessionCards?.filter((c) => c.completed).length || 0;
    const total = currentSession.totalCards || 1;
    return (completed / total) * 100;
  };

  // ==================== RENDERIZADO DE VISTAS ====================

  // Vista de configuración
  const renderConfigView = () => (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
          {t("intensiveStudy.configureSession", "Configurar Sesión Intensiva")}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "intensiveStudy.configureDesc",
            "Elige un tema y la intensidad para comenzar",
          )}
        </p>
      </div>

      {/* Alerta de sesión activa existente */}
      {activeSessionError && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-amber-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {t(
                  "intensiveStudy.activeSessionTitle",
                  "Sesión Activa Existente",
                )}
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {activeSessionError}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleContinueSession}
                  disabled={resuming}
                  className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800/50 rounded hover:bg-amber-200 dark:hover:bg-amber-700/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {t("intensiveStudy.continueSession", "Continuar Sesión")}
                </button>
                <button
                  onClick={async () => {
                    const active = await getActiveSession();
                    if (!active) {
                      // Si no hay sesión activa, limpiar el error
                      setActiveSessionError(null);
                      return;
                    }

                    if (
                      !window.confirm(
                        t(
                          "intensiveStudy.confirmAbandon",
                          "¿Estás seguro de abandonar la sesión? Perderás XP.",
                        ),
                      )
                    ) {
                      return;
                    }

                    // El aviso sólo desaparece si el backend confirmó el
                    // abandono; si falla, el error local sigue visible.
                    const outcome = await abandonSession(active.id);
                    if (outcome.status !== "success") {
                      return;
                    }

                    setActiveSessionError(null);
                    await fetchSessions();
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-800/50 rounded hover:bg-red-200 dark:hover:bg-red-700/50 transition-colors"
                >
                  {t("intensiveStudy.abandonSession", "Abandonar")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selector de tema */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("intensiveStudy.selectTopic", "Seleccionar tema")}
        </label>
        <select
          value={selectedTopicId ?? ""}
          onChange={(e) =>
            setSelectedTopicId(e.target.value ? Number(e.target.value) : null)
          }
          disabled={loadingTopics}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">
            {t("intensiveStudy.chooseTopic", "Elige un tema...")}
          </option>
          {topics.map((topic) => (
            <option key={topic.id} value={String(topic.id)}>
              {topic.name}
            </option>
          ))}
        </select>
      </div>

      {/* Selector de intensidad */}
      <IntensityPicker
        selectedIntensity={selectedIntensity}
        onSelect={setSelectedIntensity}
        disabled={loading}
      />

      {/* Botón de iniciar */}
      <div className="flex justify-center">
        <button
          onClick={handleCreateSession}
          disabled={!selectedTopicId || !selectedIntensity || loading}
          className={`
            flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg transition-all
            ${
              selectedTopicId && selectedIntensity && !loading
                ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
            }
          `}
        >
          <Play className="w-5 h-5" />
          {t("intensiveStudy.startSession", "Iniciar Sesión")}
        </button>
      </div>

      {error && (
        <p className="text-center text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );

  // Vista de sesión lista (esperando iniciar Pomodoro)
  const renderReadyView = () => (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
          {t("intensiveStudy.sessionReady", "Sesión Lista")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t(
            "intensiveStudy.readyDesc",
            "Tu sesión está activa y lista para comenzar",
          )}
        </p>
      </div>

      {/* Timer en 0, pausado */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
        <PomodoroTimer
          timeRemaining={0}
          totalTime={25 * 60} // 25 minutos por defecto
          phase="WORK"
          blockNumber={1}
          totalBlocks={totalBlocks}
          isPaused={true}
        />
      </div>

      {/* Información de la sesión */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl p-6">
        <h3 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-4">
          {t("intensiveStudy.sessionInfo", "Información de la sesión")}
        </h3>
        <div className="grid grid-cols-2 gap-4 text-left">
          <div>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              {t("intensiveStudy.topic", "Tema")}
            </p>
            <p className="font-medium text-indigo-800 dark:text-indigo-200">
              {currentSession?.topic?.name}
            </p>
          </div>
          <div>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              {t("intensiveStudy.intensity", "Intensidad")}
            </p>
            <p className="font-medium text-indigo-800 dark:text-indigo-200 capitalize">
              {currentSession?.intensity?.toLowerCase()}
            </p>
          </div>
          <div>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              {t("intensiveStudy.totalCards", "Total de tarjetas")}
            </p>
            <p className="font-medium text-indigo-800 dark:text-indigo-200">
              {currentSession?.totalCards}
            </p>
          </div>
          <div>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              {t("intensiveStudy.pomodoros", "Pomodoros")}
            </p>
            <p className="font-medium text-indigo-800 dark:text-indigo-200">
              {totalBlocks}
            </p>
          </div>
        </div>
      </div>

      {/* Botón para iniciar Pomodoro */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={handleStartFirstPomodoro}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold rounded-xl transition-colors"
        >
          <Play className="w-5 h-5" />
          {t("intensiveStudy.startPomodoro", "Iniciar Bloque Pomodoro")}
        </button>

        <button
          onClick={handleAbandon}
          className="flex items-center justify-center gap-2 px-8 py-4 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 font-semibold rounded-xl transition-colors"
        >
          <Square className="w-5 h-5" />
          {t("intensiveStudy.abandonSession", "Abandonar Sesión")}
        </button>
      </div>
    </div>
  );

  // Vista de sesión activa
  const renderActiveView = () => (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header con timer y controles */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePause}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <Pause className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        </button>

        <PomodoroTimer
          timeRemaining={pomodoroTimer.timeRemaining}
          totalTime={pomodoroTimer.totalTime}
          phase={pomodoroTimer.phase as "WORK" | "SHORT_BREAK" | "LONG_BREAK"}
          blockNumber={pomodoroTimer.blockNumber}
          totalBlocks={totalBlocks}
          isPaused={!pomodoroTimer.isRunning}
        />

        <button
          onClick={handleAbandon}
          className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
        >
          <X className="w-5 h-5 text-red-600" />
        </button>
      </div>

      {/* Progreso */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${getProgress()}%` }}
        />
      </div>
      {/* BLOQUE A REVISAR*/}
      <p className="text-center text-sm text-gray-600 dark:text-gray-400">
        {currentSession?.completedCards || 0} /{" "}
        {currentSession?.totalCards || 0}{" "}
        {t("intensiveStudy.cards", "tarjetas")}
        {" • "}
        {t("intensiveStudy.pomodoro", "Pomodoro")} {pomodoroTimer.blockNumber} /{" "}
        {totalBlocks}
      </p>

      {/* Panel de bloque completado: se revisaron todas las tarjetas del bloque,
          ya sea que el timer siga corriendo o que ya haya vencido; en ambos casos
          ofrece pasar al descanso. El caso vencido es deliberado: sin él, reanudar
          un bloque expirado dejaba la vista congelada en 0:00 sin ninguna acción.
          Solo es válido tras la hidratación autoritativa. */}
      {shouldShowBlockComplete({
        hydrated,
        card: currentCard,
      }) && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl p-6 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-800/50 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200">
                {t("intensiveStudy.blockCardsComplete", "¡Bloque completado!")}
              </h3>
              <p className="text-emerald-600 dark:text-emerald-400 mt-1">
                {pomodoroTimer.timeRemaining === 0
                  ? t(
                      "intensiveStudy.blockExpiredDesc",
                      "El tiempo de este bloque ya terminó. Continúa al descanso.",
                    )
                  : t(
                      "intensiveStudy.blockCardsCompleteDesc",
                      "Has revisado todas las tarjetas de este bloque. Esperando a que termine el tiempo.",
                    )}
              </p>
            </div>
            <button
              onClick={handlePomodoroComplete}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <SkipForward className="w-5 h-5" />
              {t("intensiveStudy.goToBreak", "Ir al descanso ahora")}
            </button>
          </div>
        </div>
      )}

      {/* Tarjeta actual */}
      {currentCard && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Pregunta */}
          <div className="p-8">
            <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-4">
              {t("intensiveStudy.question", "Pregunta")}
            </h3>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-xl text-gray-800 dark:text-white whitespace-pre-wrap">
                {currentCard.card?.question}
              </p>
            </div>
          </div>

          {/* Respuesta (si se muestra) */}
          {showAnswer && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-8 bg-gray-50 dark:bg-gray-700/50">
              <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-4">
                {t("intensiveStudy.answer", "Respuesta")}
              </h3>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-xl text-gray-800 dark:text-white whitespace-pre-wrap">
                  {currentCard.card?.answer}
                </p>
              </div>
            </div>
          )}

          {/* Controles de la tarjeta */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 space-y-4">
            {!showAnswer ? (
              <button
                onClick={() => setShowAnswer(true)}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Eye className="w-5 h-5" />
                {t("intensiveStudy.showAnswer", "Mostrar Respuesta")}
              </button>
            ) : (
              <>
                <SessionDifficultySelector
                  onSelect={setSelectedDifficulty}
                  selectedDifficulty={selectedDifficulty}
                />

                <button
                  onClick={handleCardComplete}
                  disabled={!selectedDifficulty}
                  className={`
                    w-full py-3 font-medium rounded-lg transition-colors flex items-center justify-center gap-2
                    ${
                      selectedDifficulty
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed"
                    }
                  `}
                >
                  <Check className="w-5 h-5" />
                  {t("intensiveStudy.nextCard", "Siguiente Tarjeta")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Vista de descanso
  const renderBreakView = () => (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
          {t("intensiveStudy.takeBreak", "¡Toma un Descanso!")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("intensiveStudy.breakDesc", "Has completado un bloque de trabajo")}
        </p>
      </div>

      <PomodoroTimer
        timeRemaining={pomodoroTimer.timeRemaining}
        totalTime={pomodoroTimer.totalTime}
        phase={pomodoroTimer.phase as "WORK" | "SHORT_BREAK" | "LONG_BREAK"}
        blockNumber={pomodoroTimer.blockNumber}
        totalBlocks={totalBlocks}
        isPaused={!pomodoroTimer.isRunning}
        onSkipBreak={handleSkipBreak}
      />

      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t(
          "intensiveStudy.breakMotivation",
          "Tu cerebro te lo agradecerá. Usa este tiempo para estirarte o hidratarte.",
        )}
      </div>
    </div>
  );

  // Vista de pausa
  const renderPausedView = () => (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
          {t("intensiveStudy.sessionPaused", "Sesión Pausada")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("intensiveStudy.pausedDesc", "Tu sesión está en pausa")}
        </p>
      </div>

      {abandonInfo && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-6">
          <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
            {t("intensiveStudy.abandonPenalty", "Penalización por abandonar")}
          </h3>
          <p className="text-amber-700 dark:text-amber-300">
            {abandonInfo.message}
          </p>
          <p className="text-xl font-bold text-amber-800 dark:text-amber-200 mt-2">
            -{abandonInfo.xpToLose} XP
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={handleResume}
          className="flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
        >
          <Play className="w-5 h-5" />
          {t("intensiveStudy.resumeSession", "Reanudar Sesión")}
        </button>

        <button
          onClick={handleAbandon}
          className="flex items-center justify-center gap-2 px-8 py-4 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 font-semibold rounded-xl transition-colors"
        >
          <Square className="w-5 h-5" />
          {t("intensiveStudy.abandonSession", "Abandonar Sesión")}
        </button>
      </div>
    </div>
  );

  // Vista de resultados
  const renderResultsView = () => (
    <SessionResultsSummary
      session={sessionResults || (currentSession as IntensiveSessionDetail)}
      summary={sessionSummary}
      totalBlocks={totalBlocks}
      newBadges={newBadges}
      intradayReviews={intradayReviews}
      onClose={handleBackToConfig}
    />
  );

  // Error accionable fuera de CONFIG: la vista de configuración ya tiene su
  // propio aviso, así que aquí nunca se duplica.
  const visibleError = selectVisibleIntensiveError({
    view: currentView,
    commandFailure,
    hookError: error,
  });
  // Sólo un comando fallido es reintentable; un error informativo no lo es.
  const retryableCommand: IntensiveCommand | null =
    visibleError?.command ?? null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      {/* Error local de la sesión intensiva, visible en cualquier vista activa */}
      {visibleError && (
        <div
          role="alert"
          className="max-w-3xl mx-auto mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
          <p className="text-sm text-red-700 dark:text-red-300">
            {visibleError.message}
          </p>
          <div className="mt-3 flex gap-2">
            {retryableCommand && (
              <button
                onClick={() => retryCommand(retryableCommand)}
                disabled={loading}
                className="px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-800/50 rounded hover:bg-red-200 dark:hover:bg-red-700/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {t("intensiveStudy.retryCommand", "Reintentar")}
              </button>
            )}
            <button
              onClick={dismissCommandFailure}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t("intensiveStudy.dismissError", "Descartar")}
            </button>
          </div>
        </div>
      )}

      {/* Sin bloques por iniciar: el siguiente paso es completar la sesión */}
      {completionOffered && currentView !== "CONFIG" && (
        <div
          role="status"
          className="max-w-3xl mx-auto mb-6 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4"
        >
          <p className="text-sm text-indigo-800 dark:text-indigo-200">
            {t(
              "intensiveStudy.startNoBlocksLeft",
              "No quedan bloques por iniciar. Completa la sesión para ver tus resultados.",
            )}
          </p>
          <div className="mt-3">
            <button
              onClick={() => void completeOfferedSession()}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {t("intensiveStudy.actions.complete", "Completar")}
            </button>
          </div>
        </div>
      )}

      {/* Botón de volver (excepto en configuración) */}
      {currentView !== "CONFIG" &&
        currentView !== "RESULTS" &&
        currentView !== "READY" && (
          <button
            onClick={() =>
              currentView === "PAUSED" ? handleResume() : handlePause()
            }
            className="fixed top-20 left-4 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow z-10"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
        )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Vistas */}
      {!loading && (
        <>
          {currentView === "CONFIG" && renderConfigView()}
          {currentView === "READY" && renderReadyView()}
          {currentView === "ACTIVE" && renderActiveView()}
          {currentView === "BREAK" && renderBreakView()}
          {currentView === "PAUSED" && renderPausedView()}
          {currentView === "RESULTS" && renderResultsView()}
        </>
      )}
    </div>
  );
};

export default IntensiveStudy;
